import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, LinearProgress, Table, TableHead,
  TableBody, TableRow, TableCell, IconButton, TextField, CircularProgress, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useTranslation } from 'react-i18next';
import WmsBarcodeScanner from '../../shared/WmsBarcodeScanner';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import countingService, { TaskLocationDto, CountedProduct } from '../../services/countingService';
import { buildBarcodeVariants, matchesScannedValue, matchesScannedLocation } from '../../shared/scanMatching';

interface CountingFlowProps {
  taskId: string;
  taskNumber: string;
  taskName: string;
  countType?: string;
  locations: TaskLocationDto[];
  onLocationUpdate: (locations: TaskLocationDto[]) => void;
  onAllDone: () => void;
  // Hızlı sayımda lokasyon zaten okutulmuştur; ilk lokasyon için ürün adımına
  // otomatik geçilir (tekrar lokasyon okutma gerekmez).
  autoStartFirst?: boolean;
  onRequestCancel?: () => void;
}

type Step = 'scan-location' | 'count-products';

const CountingFlow: React.FC<CountingFlowProps> = ({
  taskId, taskNumber, taskName, countType, locations, onLocationUpdate, onAllDone, autoStartFirst, onRequestCancel,
}) => {
  const { t } = useTranslation('warehouse');
  const { notification, notify, clear } = useWmsNotification();
  const [step, setStep] = useState<Step>('scan-location');
  const [currentIdx, setCurrentIdx] = useState(() => {
    const firstUncounted = locations.findIndex(l => !l.isCounted);
    return firstUncounted >= 0 ? firstUncounted : 0;
  });
  const [countedProducts, setCountedProducts] = useState<CountedProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  // Sistem ürünleri yüklenemezse (ör. eski backend / ağ hatası) sessiz boş liste yerine bunu gösteririz.
  const [productsLoadError, setProductsLoadError] = useState(false);

  const currentLocation = locations[currentIdx];
  const countedCount = locations.filter(l => l.isCounted).length;
  const progress = locations.length > 0 ? (countedCount / locations.length) * 100 : 0;

  // Lokasyon onaylandığında sistemdeki ürünleri getir ve sayım listesini bunlarla
  // ön-doldur. Operatör "burada ne var, kaç adet" görüp onaylar/düzeltir (Fix C).
  const beginCountForIndex = useCallback(async (idx: number) => {
    const loc = locations[idx];
    if (!loc) return;
    setCurrentIdx(idx);
    setStep('count-products');
    setCountedProducts([]);
    setProductsLoadError(false);
    setLoadingProducts(true);
    try {
      const prods = await countingService.getLocationProducts(taskId, loc.locationId);
      setCountedProducts(prods.map(p => ({
        productId: p.productId,
        productName: p.productName,
        barcode: p.barcode,
        countedQuantity: Math.round(p.systemQuantity),
        systemQuantity: p.systemQuantity,
      })));
    } catch (err: any) {
      // Yükleme başarısız: boş sanılmasın. Kullanıcıyı uyar, ürünü elle okutabilsin.
      setProductsLoadError(true);
      setCountedProducts([]);
      notify({
        type: 'error',
        message: err?.response?.data?.message || err?.response?.data?.Message || t('screens.counting.flow.productsLoadError'),
        duration: 5000,
      });
    }
    setLoadingProducts(false);
  }, [locations, taskId, notify, t]);

  // Hızlı sayım: lokasyon zaten okutulduğu için ilk lokasyonun ürün adımına
  // otomatik geç. Sadece bir kez çalışır.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartFirst && !autoStartedRef.current && locations.length > 0) {
      autoStartedRef.current = true;
      const idx = locations.findIndex(l => !l.isCounted);
      beginCountForIndex(idx >= 0 ? idx : 0);
    }
  }, [autoStartFirst, locations, beginCountForIndex]);

  const handleScanLocation = useCallback((barcode: string) => {
    // Serbest sıra: görevdeki HERHANGİ bir lokasyon okutulabilir (katı sıra dayatılmaz).
    const matchedIdx = locations.findIndex(l =>
      matchesScannedLocation(barcode, l.locationCode)
      || matchesScannedLocation(barcode, l.barcodeData)
      || matchesScannedLocation(barcode, l.locationId)
    );

    if (matchedIdx < 0) {
      notify({ type: 'error', message: t('screens.counting.flow.locationNotInTask') });
      return;
    }

    const loc = locations[matchedIdx];
    if (matchedIdx !== currentIdx) {
      notify({ type: 'info', message: t('screens.counting.flow.jumpedToLocation', { code: loc.locationCode }) });
    } else {
      notify({ type: 'success', message: t('screens.counting.flow.locationVerified', { code: loc.locationCode }) });
    }
    beginCountForIndex(matchedIdx);
  }, [locations, currentIdx, beginCountForIndex, notify, t]);

  const handleScanProduct = useCallback(async (barcode: string) => {
    try {
      const { accountingApi } = await import('../../../../config/apiClient');
      const { ACCOUNTING_API } = await import('../../../../config/api');
      const barcodeVariants = Array.from(buildBarcodeVariants(barcode));
      const searchTerms = barcodeVariants.length > 0 ? barcodeVariants : [String(barcode || '').trim()];

      let items: any[] = [];
      for (const term of searchTerms) {
        const resp = await accountingApi.post(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, {
          PageNumber: 1,
          PageSize: 20,
          SearchTerm: term,
          IsActive: true,
          HideZeroStock: false,
        });
        const payloadItems =
          resp.data?.items ||
          resp.data?.Items ||
          resp.data?.data?.items ||
          resp.data?.data?.Items ||
          resp.data?.data ||
          [];
        if (Array.isArray(payloadItems) && payloadItems.length > 0) {
          items = payloadItems;
          break;
        }
      }

      if (items.length === 0) {
        // Bazi tenant ortamlarda paged endpoint barkod aramasinda bos donebiliyor.
        // Bu durumda tum item listesinden istemci tarafinda barkod eslestiriyoruz.
        const fallback = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEMS);
        const allItems = Array.isArray(fallback.data)
          ? fallback.data
          : (fallback.data?.data || fallback.data?.items || fallback.data?.Items || []);
        items = (Array.isArray(allItems) ? allItems : []).filter((p: any) => (
          matchesScannedValue(barcode, p.barcode || p.Barcode)
          || matchesScannedValue(barcode, p.stockCode || p.StockCode)
          || matchesScannedValue(barcode, p.sku || p.Sku)
          || matchesScannedValue(barcode, p.id || p.Id)
        ));
      }

      if (items.length === 0) {
        notify({ type: 'warning', message: t('screens.counting.flow.productNotFound') });
        return;
      }

      const matched =
        items.find((p: any) => matchesScannedValue(barcode, p.barcode || p.Barcode))
        || items.find((p: any) => matchesScannedValue(barcode, p.stockCode || p.StockCode))
        || items.find((p: any) => matchesScannedValue(barcode, p.sku || p.Sku))
        || items[0];

      const productId = matched.id || matched.Id;
      const productName = matched.name || matched.Name || matched.itemName || matched.ItemName;
      const productBarcode = matched.barcode || matched.Barcode || barcode;

      if (!productId) {
        notify({ type: 'warning', message: t('screens.counting.flow.productNotFound') });
        return;
      }

      setCountedProducts(prev => {
        const existing = prev.find(p => p.productId === productId);
        if (existing) {
          return prev.map(p =>
            p.productId === productId ? { ...p, countedQuantity: p.countedQuantity + 1 } : p
          );
        }
        return [...prev, {
          productId,
          productName: productName || t('screens.counting.flow.productFallback'),
          barcode: productBarcode,
          countedQuantity: 1,
        }];
      });
      notify({ type: 'success', message: t('screens.counting.flow.productPlus', { name: productName || t('screens.counting.flow.productFallback') }) });
    } catch (err: any) {
      notify({
        type: 'error',
        message: err?.response?.data?.message || err?.response?.data?.Message || t('screens.counting.flow.productSearchError'),
      });
    }
  }, [notify, t]);

  const handleQtyChange = useCallback((productId: string, delta: number) => {
    setCountedProducts(prev => prev.map(p =>
      p.productId === productId ? { ...p, countedQuantity: Math.max(0, p.countedQuantity + delta) } : p
    ));
  }, []);

  const handleRemoveProduct = useCallback((productId: string) => {
    setCountedProducts(prev => prev.filter(p => p.productId !== productId));
  }, []);

  const handleEmptyLocation = useCallback(async () => {
    if (!currentLocation) return;
    try {
      await countingService.countLocation(taskId, currentLocation.locationId, [], true);
      notify({ type: 'info', message: t('screens.counting.flow.emptyLocationSaved') });
      advanceToNext();
    } catch {
      notify({ type: 'error', message: t('screens.counting.flow.saveError') });
    }
  }, [currentLocation, taskId, notify, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompleteLocation = useCallback(async () => {
    if (!currentLocation) return;
    try {
      await countingService.countLocation(taskId, currentLocation.locationId, countedProducts, false);
      notify({ type: 'success', message: t('screens.counting.flow.locationCountSaved', { code: currentLocation.locationCode }) });
      advanceToNext();
    } catch {
      notify({ type: 'error', message: t('screens.counting.flow.saveError') });
    }
  }, [currentLocation, taskId, countedProducts, notify, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const advanceToNext = () => {
    const updated = locations.map((l, i) => i === currentIdx ? { ...l, isCounted: true } : l);
    onLocationUpdate(updated);

    const nextIdx = updated.findIndex((l, i) => i > currentIdx && !l.isCounted);
    if (nextIdx >= 0) {
      setCurrentIdx(nextIdx);
      setStep('scan-location');
      setCountedProducts([]);
    } else {
      const anyLeft = updated.findIndex(l => !l.isCounted);
      if (anyLeft >= 0) {
        setCurrentIdx(anyLeft);
        setStep('scan-location');
        setCountedProducts([]);
      } else {
        onAllDone();
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <WmsNotification notification={notification} onClose={clear} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('screens.counting.flow.title', { taskNumber })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {taskName}
          </Typography>
          {onRequestCancel && (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<ReportProblemIcon />}
              onClick={onRequestCancel}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('screens.counting.cancelRequest.reportBtn')}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          {t('screens.counting.flow.progress', { count: countedCount, total: locations.length })}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <LinearProgress variant="determinate" value={progress}
            sx={{ height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': { borderRadius: 5 } }} />
        </Box>
      </Box>

      {countType === 'Random' && (
        <Typography variant="caption" color="text.secondary">
          {t('screens.counting.flow.randomOrderHint')}
        </Typography>
      )}

      {countType === 'ProductList' && (
        <Typography variant="caption" color="text.secondary">
          {t('screens.counting.flow.productListHint')}
        </Typography>
      )}

      {/* Tek scanner — step geçişinde unmount olmaz, focus hiç kaybolmaz */}
      <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
          {step === 'scan-location'
            ? t('screens.counting.flow.scanLocationTitle')
            : t('screens.counting.flow.scanProductTitle')}
        </Typography>
        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
          <WmsBarcodeScanner
            onScan={step === 'scan-location' ? handleScanLocation : handleScanProduct}
            placeholder={step === 'scan-location'
              ? t('screens.counting.flow.scanLocationPlaceholder')
              : t('screens.counting.flow.scanProductPlaceholder')}
          />
        </Box>
        {step === 'count-products' && countedProducts.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            {t('screens.counting.flow.confirmHint')}
          </Typography>
        )}
      </Paper>

      {step === 'scan-location' && currentLocation && (
        <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
          <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 0.5 }}>{t('screens.counting.flow.nextLocation')}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
            {currentLocation.locationCode}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{currentLocation.locationName}</Typography>
        </Paper>
      )}

      {step === 'count-products' && currentLocation && (
        <>
          <Paper sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 2,
            borderLeft: '4px solid', borderLeftColor: 'success.main' }}>
            <Typography variant="body1">
              {t('screens.counting.flow.locationConfirmedLabel')} <strong>{currentLocation.locationCode}</strong>
            </Typography>
          </Paper>

          <Paper sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, p: 2, pb: 0 }}>
              {t('screens.counting.flow.countedProducts', { count: countedProducts.length })}
            </Typography>
            {loadingProducts ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={22} />
                <Typography color="text.secondary" sx={{ mt: 1 }}>{t('screens.counting.flow.loadingProducts')}</Typography>
              </Box>
            ) : productsLoadError ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error.main" sx={{ fontWeight: 600 }}>{t('screens.counting.flow.productsLoadError')}</Typography>
                <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => beginCountForIndex(currentIdx)}>
                  {t('screens.counting.flow.retryLoad')}
                </Button>
              </Box>
            ) : countedProducts.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">{t('screens.counting.flow.systemEmptyHint')}</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('screens.counting.flow.colProduct')}</TableCell>
                    <TableCell align="center">{t('screens.counting.flow.colSystem')}</TableCell>
                    <TableCell align="center">{t('screens.counting.flow.colCounted')}</TableCell>
                    <TableCell align="center">{t('screens.counting.flow.colAction')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {countedProducts.map(p => (
                    <TableRow key={p.productId}>
                      <TableCell>{p.productName || p.barcode}</TableCell>
                      <TableCell align="center">
                        {p.systemQuantity != null ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={p.countedQuantity === p.systemQuantity ? 'success' : 'warning'}
                            label={p.systemQuantity}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleQtyChange(p.productId, -1)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            value={p.countedQuantity}
                            onChange={e => {
                              const v = parseInt(e.target.value);
                              if (!isNaN(v) && v >= 0) {
                                setCountedProducts(prev => prev.map(pp =>
                                  pp.productId === p.productId ? { ...pp, countedQuantity: v } : pp
                                ));
                              }
                            }}
                            size="small" type="number"
                            sx={{ width: 60 }}
                            InputProps={{ inputProps: { min: 0, style: { textAlign: 'center' } } }}
                          />
                          <IconButton size="small" onClick={() => handleQtyChange(p.productId, 1)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleRemoveProduct(p.productId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Button variant="outlined" color="warning" onClick={handleEmptyLocation} size="large">
              {t('screens.counting.flow.emptyLocationBtn')}
            </Button>
            <Button variant="contained" color="success" size="large" startIcon={<CheckIcon />}
              onClick={handleCompleteLocation}
              disabled={countedProducts.length === 0 || loadingProducts}
              sx={{ minWidth: 200, fontSize: 16, fontWeight: 700 }}>
              {t('screens.counting.flow.completeLocationBtn')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CountingFlow;
